import { useState, useCallback, useEffect, useRef } from 'react';
import { useVisibility } from './useVisibility';

// Base token only - for Base Hackathon
const TOKEN_TYPES = [
  { name: "Base", image: "/logo.png", color: "#0052FF", points: 10 },
];

const ITEM_TYPES = [
  { name: "Token", symbol: "IMAGE", color: "#FF8C00", isGood: true, points: 10, spawnWeight: 0.9 },
  { name: "Bomb", symbol: "💣", color: "#ff4444", isGood: false, points: 0, spawnWeight: 0.1 }
];

const MAX_ITEMS = 12; // Limit maximum items on screen

const getRandomItemType = () => {
  const random = Math.random();
  let cumulative = 0;
  
  for (let itemType of ITEM_TYPES) {
    cumulative += itemType.spawnWeight;
    if (random <= cumulative) {
      return itemType;
    }
  }
  
  return ITEM_TYPES[0];
};

export const useGameLoop = (canvasRef, gameState, onEndGame, updateParticles, onFruitMissed, difficultyLevel = 1) => {
  const [items, setItems] = useState([]);
  const [slashTrail, setSlashTrail] = useState([]);
  const [particles, setParticles] = useState([]);
  const [tokenImages, setTokenImages] = useState({});
  const isVisible = useVisibility();
  const penalizedFruits = useRef(new Set()); // Track fruits that already had penalties applied

  // Load all token images with better error handling
  useEffect(() => {
    const loadedImages = {};
    let loadedCount = 0;
    const totalTokens = TOKEN_TYPES.length;
    
    console.log(`🔄 Loading Base token image for Base Hackathon...`);
    
    TOKEN_TYPES.forEach((token) => {
      const img = new Image();
      
      // Set crossOrigin if needed for CORS
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        loadedImages[token.name] = img;
        loadedCount++;
        console.log(`✅ Loaded ${token.name} (${loadedCount}/${totalTokens})`);
        
        if (loadedCount === totalTokens) {
          setTokenImages(loadedImages);
          console.log('✨ Base token image loaded successfully!');
        }
      };
      
      img.onerror = (error) => {
        console.error(`❌ Failed to load ${token.name} from ${token.image}`, error);
        // Still increment count to prevent hanging
        loadedCount++;
        
        if (loadedCount === totalTokens) {
          setTokenImages(loadedImages);
          console.log(`⚠️ Token loading complete with ${totalTokens - Object.keys(loadedImages).length} errors`);
        }
      };
      
      // Set source last to trigger loading
      img.src = token.image;
    });
    
    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up token image loaders');
    };
  }, []);

  // Clean up items when tab becomes visible again to prevent accumulation
  useEffect(() => {
    if (isVisible && items.length > MAX_ITEMS) {
      setItems(prev => prev.slice(-MAX_ITEMS)); // Keep only the most recent items
    }
  }, [isVisible, items.length]);

  // Clear all trails when tab is not visible to prevent memory issues
  useEffect(() => {
    if (!isVisible) {
      setItems(prev => prev.map(item => ({ ...item, trail: [] })));
    }
  }, [isVisible]);

  const spawnItem = useCallback(() => {
    if (!gameState.isGameRunning || gameState.isPaused || !canvasRef.current || !isVisible) return;
    
    // Prevent spawning if there are too many items already
    if (items.length >= MAX_ITEMS) return;
    
    const canvas = canvasRef.current;
    const itemType = getRandomItemType();
    
    // Randomly select a token for good items
    const randomToken = TOKEN_TYPES[Math.floor(Math.random() * TOKEN_TYPES.length)];
    
    // Calculate progressive difficulty based on elapsed time
    let speedMultiplier = 1;
    
    if (gameState.gameStartTime) {
      const elapsed = Date.now() - gameState.gameStartTime;
      
      // Progressive difficulty every 10 seconds
      if (elapsed > 10000) {
        const difficultyLevel = Math.floor(elapsed / 10000); // Level 1 at 10s, 2 at 20s, etc.
        speedMultiplier = 1 + (difficultyLevel * 0.4); // 40% faster each level
      }
    }
    
    // Vertical-only motion patterns with minimal or no horizontal movement
    const motionPattern = Math.floor(Math.random() * 3); // 3 motion types, all mostly vertical
    const baseSpeed = 3 + Math.random() * 2; // Increased vertical speed for challenge
    
    let vx, vy, motionType;
    
    switch(motionPattern) {
      case 0: // Pure straight fall
        vx = 0; // No horizontal movement
        vy = baseSpeed * speedMultiplier;
        motionType = 'straight';
        break;
      case 1: // Slight wobble (minimal horizontal)
        vx = (Math.random() - 0.5) * 0.5 * speedMultiplier; // Very minimal horizontal
        vy = baseSpeed * speedMultiplier;
        motionType = 'wobble';
        break;
      case 2: // Fast straight drop
        vx = 0; // No horizontal movement
        vy = (baseSpeed * 1.3) * speedMultiplier; // Faster vertical speed
        motionType = 'fast';
        break;
      default: // Fallback to straight drop
        vx = 0;
        vy = baseSpeed * speedMultiplier;
        motionType = 'straight';
        break;
    }
    
    const item = {
      id: Math.random(),
      x: Math.random() * (canvas.width - 100) + 50, // Spawn across the width
      y: -40,
      vx: vx,
      vy: vy,
      radius: itemType.name === 'Bomb' ? 28 : 38, // Larger radius for better visibility
      rotation: 0,
      rotationSpeed: (Math.random() - 0.5) * 0.15 * speedMultiplier,
      type: itemType,
      token: itemType.isGood ? randomToken : null, // Assign random token if it's a good item
      slashed: false,
      penaltyApplied: false,
      trail: [],
      motionType: motionType,
      motionTime: 0,
      amplitude: 10, // Minimal amplitude for tiny wobble only
      frequency: 0.1, // Slow frequency for gentle motion
      hitBox: itemType.name === 'Bomb' ? 35 : 45
    };
    
    setItems(prev => [...prev, item]);
  }, [gameState.isGameRunning, gameState.isPaused, gameState.gameStartTime, canvasRef, isVisible, items.length]);

  const updateGame = useCallback(() => {
    if (!gameState.isGameRunning || gameState.isPaused || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const now = Date.now();
    
    setItems(prev => prev
      .map(item => {
        item.motionTime += 0.1; // Increment motion time
        
        let newX, newY;
        
        // Apply vertical-only motion patterns
        switch(item.motionType) {
          case 'straight':
            // Pure vertical fall
            newX = item.x + item.vx;
            newY = item.y + item.vy;
            break;
            
          case 'wobble':
            // Very slight wobble while falling
            const wobbleOffset = Math.sin(item.motionTime * 0.15) * 8; // Tiny wobble
            newX = item.x + item.vx + wobbleOffset;
            newY = item.y + item.vy;
            break;
            
          case 'fast':
            // Fast straight drop
            newX = item.x; // No horizontal movement at all
            newY = item.y + item.vy;
            break;
            
          default:
            newX = item.x + item.vx;
            newY = item.y + item.vy;
        }
        
        // Only update trails if tab is visible to prevent accumulation
        let filteredTrail = item.trail || [];
        if (isVisible) {
          // Add current position to trail
          const newTrail = [...item.trail, { 
            x: item.x, 
            y: item.y, 
            timestamp: now,
            alpha: 1.0
          }];
          
          // Remove trail points older than 1 second and update alpha
          filteredTrail = newTrail
            .map(point => ({
              ...point,
              alpha: Math.max(0, 1 - (now - point.timestamp) / 1000) // Fade over 1 second
            }))
            .filter(point => point.alpha > 0)
            .slice(-8); // Keep only last 8 points for performance
        }
        
        // Check for missed fruit penalty (only once per fruit using robust tracking)
        let updatedPenaltyApplied = item.penaltyApplied;
        const shouldPenalize = newY > canvas.height + 50 && 
                              item.type.isGood && 
                              !item.slashed && 
                              !updatedPenaltyApplied && 
                              !penalizedFruits.current.has(item.id);
                              
        if (shouldPenalize && onFruitMissed) {
          console.log(`🍊 FRUIT MISSED! ID: ${item.id}, Y: ${newY}`);
          updatedPenaltyApplied = true;
          penalizedFruits.current.add(item.id);
          console.log(`✅ Fruit ID ${item.id} marked for penalty. Total penalized: ${penalizedFruits.current.size}`);
          
          // Queue penalty call to prevent render-time state updates
          requestAnimationFrame(() => {
            if (penalizedFruits.current.has(item.id)) {
              onFruitMissed();
            }
          });
        }
        
        // Log bomb missed (no penalty)
        if (newY > canvas.height + 50 && !item.type.isGood && !item.slashed) {
          console.log('💣 BOMB MISSED! No penalty - bomb fell off screen.');
        }

        return {
          ...item,
          x: newX,
          y: newY,
          rotation: item.rotation + item.rotationSpeed,
          trail: filteredTrail,
          penaltyApplied: updatedPenaltyApplied
        };
      })
      .filter(item => {
        const shouldKeep = item.y <= canvas.height + 100 && 
                          item.x >= -100 && 
                          item.x <= canvas.width + 100;
        
        // Clean up penalized fruits set when items are removed
        if (!shouldKeep && penalizedFruits.current.has(item.id)) {
          penalizedFruits.current.delete(item.id);
          console.log(`🧹 Cleaned up penalized fruit ID: ${item.id}. Remaining: ${penalizedFruits.current.size}`);
        }
        
        return shouldKeep;
      })
    );

    updateParticles();
  }, [gameState.isGameRunning, gameState.isPaused, updateParticles, canvasRef, isVisible, onFruitMissed]);

  const render = useCallback((ctx, itemsToRender, trail, particlesToRender) => {
    if (!ctx || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    
    // Clear canvas completely transparent to show wooden background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw items (aptos tokens and bombs)
    itemsToRender.forEach(item => {
      if (item.slashed) return;
      
      // Draw fluid trail first (before the object)
      if (item.trail && item.trail.length > 1) {
        ctx.save();
        
        // Create gradient trail
        for (let i = 1; i < item.trail.length; i++) {
          const prev = item.trail[i - 1];
          const curr = item.trail[i];
          
          if (prev && curr) {
            ctx.strokeStyle = item.type.name === 'Bomb' 
              ? `rgba(255, 68, 68, ${curr.alpha * 0.6})` // Red trail for bombs
              : `rgba(255, 215, 0, ${curr.alpha * 0.6})`; // Yellow trail for tokens
            
            ctx.lineWidth = Math.max(1, 3 * curr.alpha); // Thin trail that fades
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            
            ctx.beginPath();
            ctx.moveTo(prev.x, prev.y);
            ctx.lineTo(curr.x, curr.y);
            ctx.stroke();
          }
        }
        
        ctx.restore();
      }
      
      ctx.save();
      ctx.translate(item.x, item.y);
      ctx.rotate(item.rotation);
      
      // Draw item shadow
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.beginPath();
      ctx.arc(2, 2, item.radius, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw item
      if (item.type.name === 'Bomb') {
        // Special styling for bombs
        ctx.fillStyle = '#2A2A2A';
        ctx.beginPath();
        ctx.arc(0, 0, item.radius, 0, Math.PI * 2);
        ctx.fill();
        
        // Add bomb border
        ctx.strokeStyle = '#FF4444';
        ctx.lineWidth = 3;
        ctx.stroke();
        
        // Draw bomb emoji
        ctx.font = `${item.radius}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(item.type.symbol, 0, 0);
      } else {
        // Draw Token with image
        ctx.save();
        
        // Draw circular background with subtle glow
        const gradient = ctx.createRadialGradient(
          0, 0, 0,
          0, 0, item.radius * 1.1
        );
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.7)');
        gradient.addColorStop(1, item.token?.color || '#FF8C00');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(0, 0, item.radius, 0, Math.PI * 2);
        ctx.fill();
        
        // Add subtle outer glow
        ctx.shadowColor = item.token?.color || '#FF8C00';
        ctx.shadowBlur = 20;
        ctx.strokeStyle = item.token?.color || '#FF8C00';
        ctx.lineWidth = 3;
        ctx.stroke();
        
        // Reset shadow for image drawing
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        
        // Draw token image if loaded - larger and centered
        if (item.token && tokenImages[item.token.name]) {
          const img = tokenImages[item.token.name];
          const imgSize = item.radius * 1.6; // Larger image size
          
          // Ensure image is fully loaded before drawing
          if (img.complete && img.naturalHeight !== 0) {
            ctx.drawImage(img, -imgSize / 2, -imgSize / 2, imgSize, imgSize);
          } else {
            // Fallback while loading
            ctx.fillStyle = item.token?.color || '#FF8C00';
            ctx.beginPath();
            ctx.arc(0, 0, item.radius * 0.5, 0, Math.PI * 2);
            ctx.fill();
          }
        } else {
          // Fallback: draw colored circle with token name initial
          ctx.fillStyle = item.token?.color || '#FF8C00';
          ctx.beginPath();
          ctx.arc(0, 0, item.radius * 0.6, 0, Math.PI * 2);
          ctx.fill();
          
          // Draw first letter of token name
          if (item.token?.name) {
            ctx.fillStyle = '#FFFFFF';
            ctx.font = `bold ${item.radius * 0.8}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(item.token.name[0], 0, 0);
          }
        }
        
        ctx.restore();
      }
      
      ctx.restore();
    });
    
    // Draw slash trail
    if (trail.length > 1) {
      ctx.save();
      ctx.strokeStyle = '#FF6B35';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowColor = '#FF6B35';
      ctx.shadowBlur = 15;
      
      ctx.beginPath();
      ctx.moveTo(trail[0].x, trail[0].y);
      
      for (let i = 1; i < trail.length; i++) {
        ctx.lineTo(trail[i].x, trail[i].y);
      }
      
      ctx.stroke();
      ctx.restore();
    }
    
    // Draw particles on canvas
    particlesToRender.forEach(particle => {
      ctx.save();
      ctx.globalAlpha = particle.life;
      
      if (particle.color === '#FF8C00') {
        // Orange juice splatter effect
        const juiceGradient = ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, particle.size * 2
        );
        juiceGradient.addColorStop(0, '#FFA500');
        juiceGradient.addColorStop(0.6, '#FF8C00');
        juiceGradient.addColorStop(1, 'rgba(255, 140, 0, 0.2)');
        
        ctx.fillStyle = juiceGradient;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * 1.5, 0, Math.PI * 2);
        ctx.fill();
        
        // Add juice droplet shine
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.beginPath();
        ctx.arc(particle.x - particle.size * 0.3, particle.y - particle.size * 0.3, particle.size * 0.4, 0, Math.PI * 2);
        ctx.fill();
        
        // Add orange juice streak effect
        ctx.strokeStyle = '#FF7F00';
        ctx.lineWidth = particle.size * 0.3;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(particle.x, particle.y);
        ctx.lineTo(particle.x + particle.vx * 0.5, particle.y + particle.vy * 0.5);
        ctx.stroke();
      } else {
        // Regular particle rendering
        ctx.fillStyle = particle.color;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
      }
      
      ctx.restore();
    });
  }, [canvasRef, tokenImages]);

  const clearAllItems = useCallback(() => {
    setItems([]);
    setSlashTrail([]);
    setParticles([]);
    penalizedFruits.current.clear(); // Clear penalty tracking when game resets
    console.log('🧹 Cleared all items and penalty tracking');
  }, []);

  const cleanupExcessItems = useCallback(() => {
    setItems(prev => {
      if (prev.length > MAX_ITEMS) {
        // Remove oldest items and clear their trails
        return prev.slice(-MAX_ITEMS).map(item => ({ ...item, trail: [] }));
      }
      return prev;
    });
  }, []);

  return {
    items,
    slashTrail,
    particles,
    setItems,
    setSlashTrail,
    setParticles,
    spawnItem,
    updateGame,
    render,
    clearAllItems,
    cleanupExcessItems,
    itemCount: items.length
  };
};